<script setup lang="ts">
import { RefreshCw, Terminal, Zap } from '@lucide/vue'
import { AdminDialog, RrButton, RrInput, RrSelect, RrSwitch } from '@xjlv2/admin-layout'
import type { ProviderCode, PushChannelTestResult } from './types'
import { PROVIDER_CATALOG } from './types'
import PushChannelTestBanner from './PushChannelTestBanner.vue'

export interface ChannelCredentialFields {
  [key: string]: unknown
  teamId?: string
  keyId?: string
  bundleId?: string
  privateKey?: string
  sandbox?: boolean
  appKey?: string
  masterSecret?: string
  apnsProduction?: boolean
  appId?: string
  appSecret?: string
  apiBaseUrl?: string
  projectId?: string
  serviceAccountJson?: string
  clientId?: string
  clientSecret?: string
  packageName?: string
  bundleName?: string
  baseUrl?: string
  token?: string
}

export interface ChannelCredentialForm {
  appCode: string
  provider: ProviderCode
  environment: string
  enabled: boolean
  fields: ChannelCredentialFields
}

const open = defineModel<boolean>('open', { default: false })
const form = defineModel<ChannelCredentialForm>('form', { required: true })

defineProps<{
  appCode?: string
  saving?: boolean
  testing?: boolean
  testResult?: PushChannelTestResult | null
}>()

const emit = defineEmits<{
  save: []
  test: []
}>()
</script>

<template>
  <AdminDialog
    v-model:open="open"
    :title="`配置 ${PROVIDER_CATALOG[form.provider]?.label ?? form.provider} 通道凭证`"
    width="640px"
    @cancel="open = false"
  >
    <div class="channel-dialog-content">
      <div class="dialog-tip-banner">
        <Zap :size="16" class="tip-icon" />
        <div class="tip-text">
          <strong>高安全隔离与智能合并保障：</strong>
          系统已启用凭证透明加密。脱敏掩码（例如 <code>****</code> 或
          <code>...</code>）将自动继承现有原密钥，直接保存不会造成私钥被损坏或清空。
        </div>
      </div>

      <PushChannelTestBanner :result="testResult" />

      <div class="form-grid">
        <div class="form-row">
          <label class="field-label">所属应用</label>
          <RrInput :value="appCode" disabled />
        </div>

        <div class="form-row-duo">
          <div class="form-field">
            <label class="field-label">环境 (Environment)</label>
            <RrSelect
              v-model:value="form.environment"
              :options="[
                { label: '生产环境 (PROD)', value: 'PROD' },
                { label: '沙盒环境 (SANDBOX)', value: 'SANDBOX' }
              ]"
            />
          </div>
          <div class="form-field">
            <label class="field-label">启用状态</label>
            <div class="switch-field-line">
              <RrSwitch v-model:checked="form.enabled" />
              <span class="switch-field-text">{{
                form.enabled ? '通道已启用' : '通道已停用'
              }}</span>
            </div>
          </div>
        </div>

        <template v-if="form.provider === 'APNS'">
          <div class="form-row-duo">
            <div class="form-field">
              <label class="field-label">Team ID <span class="req">*</span></label>
              <RrInput v-model:value="form.fields.teamId" placeholder="如: ABC1234567" />
            </div>
            <div class="form-field">
              <label class="field-label">Key ID <span class="req">*</span></label>
              <RrInput v-model:value="form.fields.keyId" placeholder="如: DEF8901234" />
            </div>
          </div>
          <div class="form-row">
            <label class="field-label">Bundle ID / APNs Topic <span class="req">*</span></label>
            <RrInput v-model:value="form.fields.bundleId" placeholder="如: com.company.app" />
          </div>
          <div class="form-row">
            <label class="field-label">Auth Key (.p8 私钥内容) <span class="req">*</span></label>
            <RrInput
              v-model:value="form.fields.privateKey"
              type="textarea"
              :rows="5"
              placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
            />
          </div>
          <div class="form-row">
            <label class="field-label">连接苹果沙盒网关</label>
            <RrSwitch v-model:checked="form.fields.sandbox" />
          </div>
        </template>

        <template v-else-if="form.provider === 'JPUSH'">
          <div class="form-row">
            <label class="field-label">AppKey <span class="req">*</span></label>
            <RrInput v-model:value="form.fields.appKey" placeholder="极光控制台应用 AppKey" />
          </div>
          <div class="form-row">
            <label class="field-label">MasterSecret <span class="req">*</span></label>
            <RrInput
              v-model:value="form.fields.masterSecret"
              type="password"
              placeholder="保留掩码或留空表示不修改"
            />
          </div>
          <div class="form-row">
            <label class="field-label">APNs 生产环境推送</label>
            <RrSwitch v-model:checked="form.fields.apnsProduction" />
          </div>
        </template>

        <template
          v-else-if="
            form.provider === 'WECHAT_MINIAPP_SUBSCRIBE' || form.provider === 'WECHAT_MP_TEMPLATE'
          "
        >
          <div class="form-row">
            <label class="field-label">AppID <span class="req">*</span></label>
            <RrInput v-model:value="form.fields.appId" placeholder="微信公众平台 / 小程序 AppID" />
          </div>
          <div class="form-row">
            <label class="field-label">AppSecret <span class="req">*</span></label>
            <RrInput
              v-model:value="form.fields.appSecret"
              type="password"
              placeholder="留空表示不修改已保存的 AppSecret"
            />
          </div>
          <div class="form-row">
            <label class="field-label">API 代理网关 (可选)</label>
            <RrInput
              v-model:value="form.fields.apiBaseUrl"
              placeholder="默认: https://api.weixin.qq.com"
            />
          </div>
        </template>

        <template v-else-if="form.provider === 'FCM'">
          <div class="form-row">
            <label class="field-label">Project ID</label>
            <RrInput v-model:value="form.fields.projectId" placeholder="Firebase 项目 ID" />
          </div>
          <div class="form-row">
            <label class="field-label">Service Account JSON</label>
            <RrInput
              v-model:value="form.fields.serviceAccountJson"
              type="textarea"
              :rows="6"
              placeholder="留空表示不修改；重新粘贴完整 JSON 才会替换"
            />
          </div>
        </template>

        <template v-else-if="form.provider === 'HUAWEI_PUSH'">
          <div class="form-row-duo">
            <div class="form-field">
              <label class="field-label">App ID / Client ID <span class="req">*</span></label>
              <RrInput
                v-model:value="form.fields.clientId"
                placeholder="华为开发者应用 Client ID"
              />
            </div>
            <div class="form-field">
              <label class="field-label">应用包名</label>
              <RrInput v-model:value="form.fields.packageName" placeholder="如: com.company.app" />
            </div>
          </div>
          <div class="form-row">
            <label class="field-label">Client Secret <span class="req">*</span></label>
            <RrInput
              v-model:value="form.fields.clientSecret"
              type="password"
              placeholder="保留掩码或留空表示不修改"
            />
          </div>
        </template>

        <template v-else-if="form.provider === 'HARMONY_PUSH'">
          <div class="form-row-duo">
            <div class="form-field">
              <label class="field-label">Client ID <span class="req">*</span></label>
              <RrInput v-model:value="form.fields.clientId" placeholder="Push Kit Client ID" />
            </div>
            <div class="form-field">
              <label class="field-label">Bundle Name</label>
              <RrInput
                v-model:value="form.fields.bundleName"
                placeholder="如: com.company.harmony"
              />
            </div>
          </div>
          <div class="form-row">
            <label class="field-label">Client Secret <span class="req">*</span></label>
            <RrInput
              v-model:value="form.fields.clientSecret"
              type="password"
              placeholder="保留掩码或留空表示不修改"
            />
          </div>
        </template>

        <template v-else-if="form.provider === 'XIAOMI_PUSH'">
          <div class="form-row">
            <label class="field-label">AppSecret <span class="req">*</span></label>
            <RrInput
              v-model:value="form.fields.appSecret"
              type="password"
              placeholder="小米开放平台 AppSecret"
            />
          </div>
          <div class="form-row">
            <label class="field-label">应用包名</label>
            <RrInput v-model:value="form.fields.packageName" placeholder="如: com.company.app" />
          </div>
        </template>

        <template v-else-if="form.provider === 'OPPO_PUSH'">
          <div class="form-row">
            <label class="field-label">AppKey <span class="req">*</span></label>
            <RrInput v-model:value="form.fields.appKey" placeholder="OPPO PUSH AppKey" />
          </div>
          <div class="form-row">
            <label class="field-label">MasterSecret <span class="req">*</span></label>
            <RrInput
              v-model:value="form.fields.masterSecret"
              type="password"
              placeholder="OPPO PUSH MasterSecret"
            />
          </div>
        </template>

        <template v-else-if="form.provider === 'VIVO_PUSH'">
          <div class="form-row-duo">
            <div class="form-field">
              <label class="field-label">AppID <span class="req">*</span></label>
              <RrInput v-model:value="form.fields.appId" placeholder="vivo 推送 AppID" />
            </div>
            <div class="form-field">
              <label class="field-label">AppKey <span class="req">*</span></label>
              <RrInput v-model:value="form.fields.appKey" placeholder="vivo 推送 AppKey" />
            </div>
          </div>
          <div class="form-row">
            <label class="field-label">AppSecret <span class="req">*</span></label>
            <RrInput
              v-model:value="form.fields.appSecret"
              type="password"
              placeholder="vivo 推送 AppSecret"
            />
          </div>
        </template>

        <template v-else-if="form.provider === 'GORUSH'">
          <div class="form-row">
            <label class="field-label">Gorush Gateway URL <span class="req">*</span></label>
            <RrInput
              v-model:value="form.fields.baseUrl"
              placeholder="如: http://push-gateway.internal:8088"
            />
          </div>
          <div class="form-row">
            <label class="field-label">Auth Token (可选)</label>
            <RrInput
              v-model:value="form.fields.token"
              type="password"
              placeholder="Gorush API Token"
            />
          </div>
        </template>

        <template v-else>
          <div class="dialog-mock-info">
            <Terminal :size="24" class="mock-icon" />
            <p>
              Mock
              虚拟通道是本地开发联调和自动化测试的最佳工具，无需填写任何真实的厂商密钥，调用发送时将直接模拟投递成功。
            </p>
          </div>
        </template>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer-split">
        <RrButton type="default" :disabled="testing" @click="emit('test')">
          <template #icon>
            <RefreshCw :size="14" :class="{ 'spin-icon': testing }" />
          </template>
          测试配置
        </RrButton>
        <div class="dialog-footer-right">
          <RrButton @click="open = false">取消</RrButton>
          <RrButton type="primary" :disabled="saving" @click="emit('save')">
            {{ saving ? '保存中...' : '保存通道配置' }}
          </RrButton>
        </div>
      </div>
    </template>
  </AdminDialog>
</template>

<style scoped>
.channel-dialog-content,
.form-grid {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
}
.dialog-tip-banner {
  display: flex;
  gap: var(--rr-space-2);
  padding: var(--rr-space-2) var(--rr-space-4);
  background: var(--rr-color-primary-soft);
  border: 1px solid var(--rr-color-primary-border);
  border-radius: var(--rr-radius-control);
  color: var(--rr-color-text);
  font-size: var(--rr-font-size-sm);
  line-height: var(--rr-line-height-base);
}
.dialog-tip-banner .tip-icon {
  color: var(--rr-color-primary);
  flex-shrink: 0;
}
.dialog-tip-banner code {
  background: var(--rr-color-surface-subtle);
  padding: 0 var(--rr-space-1);
  border-radius: var(--rr-radius-xs);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}
.form-row,
.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-2);
}
.form-row-duo {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--rr-space-4);
}
.field-label {
  font-size: var(--rr-font-size-sm);
  font-weight: var(--rr-font-weight-medium);
  color: var(--rr-color-text);
}
.field-label .req {
  color: var(--rr-color-danger);
}
.switch-field-line {
  display: flex;
  align-items: center;
  gap: var(--rr-space-2);
  min-height: var(--rr-space-8);
}
.switch-field-text {
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text-muted);
}
:deep(.rr-switch) {
  width: auto !important;
  flex-shrink: 0;
  align-self: flex-start;
}
.dialog-mock-info {
  display: flex;
  align-items: center;
  gap: var(--rr-space-4);
  padding: var(--rr-space-4);
  background: var(--rr-color-surface-subtle);
  border-radius: var(--rr-radius-control);
  color: var(--rr-color-text-muted);
  font-size: var(--rr-font-size-sm);
}
.mock-icon {
  color: var(--rr-color-primary);
}
.dialog-footer-split {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.dialog-footer-right {
  display: flex;
  align-items: center;
  gap: var(--rr-space-2);
}
.spin-icon {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
